import React, { useState, useEffect } from 'react';
import { Modal, Form, Dropdown } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { createLocalPlan, CreateLocalPlanParams } from 'crud/configuration_api/local_plans';
import { GetAllLocals } from 'crud/management_api/locales';
import MiniLoadingSpinner from 'components/Spinners/MiniLoadingSpinner';
import './CreateLocalPlan.scss';
import { localeData } from 'moment';

interface CreateLocalPlanProps {
  show: boolean;
  onHide: () => void;
  onPlanCreated: () => void;
  localId?: number;
  localName?: string;
  local_plans_payments_metadata?: any;
}

interface Local {
  local_id: number;
  local_name: string;
}

const CreateLocalPlan: React.FC<CreateLocalPlanProps> = ({ show, onHide, onPlanCreated, localId, localName }) => {
  const [loading, setLoading] = useState(false);
  const [localsLoading, setLocalsLoading] = useState(false);
  const [locals, setLocals] = useState<Local[]>([]);
  
  // Form state
  const [selectedLocalId, setSelectedLocalId] = useState<number | null>(null);
  const [agreedPrice, setAgreedPrice] = useState<string>('');
  const [monthDayBilling, setMonthDayBilling] = useState<string>('');
  const [visitsNumber, setVisitsNumber] = useState<string>('');
  const [contractStartDate, setContractStartDate] = useState<string>('');
  const [trialStartDate, setTrialStartDate] = useState<string>('');
  const [trialDurationDays, setTrialDurationDays] = useState<string>('');
  const [local_plans_payments_metadata, set_local_plans_payments_metadata] = useState<any>(null);
  
  // Get username from localStorage for metadata
  const user = JSON.parse(localStorage.getItem('user') ?? '{}');
  const userName = user.user_name || '';

  useEffect(() => {
    if (show) {
      if (localId) {
        setSelectedLocalId(localId);
      } else {
        fetchLocals();
      }
    }
  }, [show, localId]);

  const fetchLocals = async () => {
    setLocalsLoading(true);
    try {
      const response = await GetAllLocals();
      if (response.status === 200) {
        setLocals(response.data);
      }
    } catch (err) {
      console.error('Error fetching locals:', err);
      toast.error('Error al cargar la lista de locales');
    }
    setLocalsLoading(false);
  };

  const resetForm = () => {
    if (!localId) {
      setSelectedLocalId(null);
    }
    setAgreedPrice('');
    setMonthDayBilling('');
    setVisitsNumber('');
    setContractStartDate('');
    setTrialStartDate('');
    setTrialDurationDays('');
    set_local_plans_payments_metadata(null);
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const validateForm = (): boolean => {
    if (!selectedLocalId) {
      toast.error('Debes seleccionar un local');
      return false;
    }
    if (!agreedPrice || parseFloat(agreedPrice) <= 0) {
      toast.error('Debes ingresar un precio válido');
      return false;
    }
    const dayBilling = parseInt(monthDayBilling);
    if (!monthDayBilling || dayBilling < 1 || dayBilling > 31) {
      toast.error('El día de facturación debe estar entre 1 y 31');
      return false;
    }
    if (!contractStartDate) {
      toast.error('Debes ingresar la fecha de inicio del contrato');
      return false;
    }
    // Si tiene trial, validar duración
    if (trialStartDate && (!trialDurationDays || parseInt(trialDurationDays) <= 0)) {
      toast.error('Si hay fecha de prueba, debes indicar la duración en días');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const params: CreateLocalPlanParams = {
        agreed_price: parseFloat(agreedPrice),
        month_day_billing: parseInt(monthDayBilling),
        contract_start_date: contractStartDate,
        local_plans_payments_metadata: {
          created_by: userName
        }
      };

      if (visitsNumber) {
        params.visits_number = parseInt(visitsNumber);
      }
      if (trialStartDate) {
        params.trial_start_date = trialStartDate;
      }
      if (trialDurationDays) {
        params.trial_duration_days = parseInt(trialDurationDays);
      }

      const response = await createLocalPlan(selectedLocalId!, params);
      
      if ((response as any).status === 200 || (response as any).status === 201) {
        toast.success('Plan creado exitosamente');
        onPlanCreated();
        resetForm();
      } else {
        toast.error('Error al crear el plan');
      }
    } catch (err: any) {
      console.error('Error creating plan:', err);
      const errorMsg = err?.response?.data?.message || 'Error al crear el plan';
      toast.error(errorMsg);
    }
    setLoading(false);
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
      className="local-plan-modal"
      backdrop="static"
    >
      <Modal.Header closeButton={false}>
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">Crear Plan de Local</h2>
        </div>
        <button 
          className="modal-close-button"
          onClick={handleClose}
          disabled={loading}
        >
          <X size={20} />
        </button>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {localId && localName ? (
            <div className="mp-formgroup vertical">
              <Form.Label>Local</Form.Label>
              <div className="local-plan-info">
                {localName}
              </div>
            </div>
          ) : (
            <div className="mp-formgroup vertical">
              <Form.Label>Local *</Form.Label>
              {localsLoading ? (
                <div style={{ padding: '1rem', textAlign: 'center' }}>
                  <MiniLoadingSpinner />
                </div>
              ) : (
                <Dropdown className="local-plan-dropdown">
                  <Dropdown.Toggle 
                    variant="none" 
                    className="form-control" 
                    disabled={loading}
                    style={{ 
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    {selectedLocalId 
                      ? locals.find(l => l.local_id === selectedLocalId)?.local_name || 'Selecciona un local'
                      : 'Selecciona un local'
                    }
                  </Dropdown.Toggle>
                  <Dropdown.Menu style={{ maxHeight: '400px', overflowY: 'auto', width: '100%', minWidth: '300px' }}>
                    {locals.map(local => (
                      <Dropdown.Item 
                        key={local.local_id} 
                        onClick={() => setSelectedLocalId(local.local_id)}
                        active={selectedLocalId === local.local_id}
                      >
                        {local.local_name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          )}

          <div className="mp-formgroup vertical">
            <Form.Label>Precio Acordado (CLP) *</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="number"
              value={agreedPrice}
              onChange={(e) => setAgreedPrice(e.target.value)}
              placeholder="Ej: 50000"
              disabled={loading}
              required
              min="0"
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Día de Facturación (1-31) *</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="number"
              value={monthDayBilling}
              onChange={(e) => setMonthDayBilling(e.target.value)}
              placeholder="Ej: 15"
              disabled={loading}
              required
              min="1"
              max="31"
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Número de Visitas (Opcional)</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="number"
              value={visitsNumber}
              onChange={(e) => setVisitsNumber(e.target.value)}
              placeholder="Ej: 4"
              disabled={loading}
              min="0"
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Fecha de Inicio del Contrato *</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="date"
              value={contractStartDate}
              onChange={(e) => setContractStartDate(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Fecha de Inicio de Prueba (Opcional)</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="date"
              value={trialStartDate}
              onChange={(e) => setTrialStartDate(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Duración de Prueba (días)</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="number"
              value={trialDurationDays}
              onChange={(e) => setTrialDurationDays(e.target.value)}
              placeholder="Ej: 30"
              disabled={loading || !trialStartDate}
              min="1"
            />
          </div>

          
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <button
            type="button"
            className="btn local-plan-btn form-button-cancel"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn local-plan-btn form-button-create"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <MiniLoadingSpinner />
                <span style={{ marginLeft: '0.5rem' }}>Creando...</span>
              </>
            ) : (
              'Crear Plan'
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateLocalPlan;
